import { type StatsigGateName, getStatsigClientInstance, useGateValue } from '@coursera-web/statsig';

import type CartsV2 from 'bundles/naptimejs/resources/carts.v2';
import { GOOGLE_PARTNER_ID } from 'bundles/ownable-products-common/constants/partners';
import { usePartner } from 'bundles/payments-common/data/usePartner';

/**
 * Statsig surfaces for the GwG (Grow with Google) S12N post-purchase upsell.
 *
 * This module is the single home for the GwG upsell's Statsig logic:
 *  - the rollout gate (`gwg-s12n-ppu-gate`) and its eligibility hook, and
 *  - future A/B tests on the upsell experience (e.g. copy variants) — add those as Statsig
 *    layers/experiments here (via `useLayer` / `useExperiment` from `@coursera-web/statsig`), so the
 *    gate, eligibility, and experiment logic stay co-located. See `useGwgS12nUpsellCopyVariant` below.
 */
export const GWG_S12N_PPU_GATE: StatsigGateName = 'gwg-s12n-ppu-gate';

/**
 * Non-hook check for the GwG S12N PPU upsell rollout gate.
 *
 * Uses the client instance so it can be called from plain (non-React) utils. MUST be invoked
 * client-side at call time (never at module scope) — `getStatsigClientInstance()` needs request
 * context. See `bundles/payments-common/utils/cartCreation/createCart.ts` for the same pattern.
 */
export const isGwgS12nPpuGateEnabled = (): boolean => {
  return getStatsigClientInstance().checkGate(GWG_S12N_PPU_GATE);
};

/**
 * Whether the GwG S12N post-purchase upsell rollout is enabled for this cart.
 *
 * True when the `gwg-s12n-ppu-gate` Statsig gate is enabled AND the cart's product is tied to the
 * Google partner (partnerId 463). The partnerId is resolved from the cart's product via `usePartner`
 * (it is not available directly on the cart).
 *
 * This hook is the durable, FE-facing entry point for the GwG upsell: components should use it to
 * branch the upsell experience. It is intentionally separate from the temporary, gate-only stubs in
 * `courseraPlusUpgradeUtils` / `postPurchaseUpsell/utils` that force backend eligibility while the
 * real BE implementation is in flight.
 */
export const useIsGwgS12nPpuUpsellEnabled = (cart?: CartsV2): boolean => {
  const isGateEnabled = useGateValue(GWG_S12N_PPU_GATE);
  const { s12n, course, degreeProgram } = usePartner(cart);
  const partnerId = s12n?.partners?.[0]?.id ?? course?.partners?.[0]?.id ?? degreeProgram?.partnerIds?.[0];

  return isGateEnabled && partnerId === GOOGLE_PARTNER_ID;
};

/**
 * Copy variant for the GwG S12N upsell flow.
 *
 * Scaffold for an upcoming A/B test on upsell copy. Returns false until the experiment exists;
 * once the Statsig layer/experiment is created, back this with `useLayer`/`useExperiment` here (and
 * extend `GwgS12nUpsellCopyVariant`) so callers don't change.
 *
 * TODO(MONETIZE-2797 follow-up): wire to the GwG S12N upsell copy experiment once created in Statsig.
 */
export const isGwgS12nUpsellCopyVariantEnabled = (): boolean => {
  return false;
};
